// The way to the Monad from a table the player has made: what the Glimmer
// shows when pressed twice. The solver searches; these join its answers.

import { applyMove, layoutKey, type Move, type TableState } from "./engine.ts"
import { solve } from "./solver.ts"

// "none": every table was searched to the end and none leads on; "unknown":
// none was found, but at least one search gave up first.
export type Way =
  | { readonly tag: "way"; readonly at: number; readonly moves: readonly Move[] }
  | { readonly tag: "none" }
  | { readonly tag: "unknown" }

// The first of these tables from which a way to the Monad is found.
export const firstWay = (states: readonly TableState[], maxNodes: number): Way => {
  let gaveUp = false
  for (const [at, s] of states.entries()) {
    const r = solve(s, { maxNodes })
    if (r.tag === "solved") return { tag: "way", at, moves: r.moves }
    if (r.tag === "unknown") gaveUp = true
  }
  return gaveUp ? { tag: "unknown" } : { tag: "none" }
}

// For each table along a way, the moves still to make from it, so a player
// who follows the ghost need not wait for the solver again. Keyed by the
// exact layout: the same cards in other columns need other moves.
export const waysAlong = (start: TableState, moves: readonly Move[]): ReadonlyMap<string, readonly Move[]> => {
  const ways = new Map<string, readonly Move[]>()
  let s = start
  for (const [i, m] of moves.entries()) {
    ways.set(layoutKey(s), moves.slice(i))
    const r = applyMove(s, m)
    if (r.tag === "error") break
    s = r.value.state
  }
  return ways
}

// The moves still to make from this table, if a way passed through it.
export const wayFrom = (ways: ReadonlyMap<string, readonly Move[]>, state: TableState): readonly Move[] | undefined => ways.get(layoutKey(state))
